import React, {
    ButtonHTMLAttributes,
    ReactElement,
    ReactNode,
} from "react";
import classNames from "classnames";
import { ReactComponent as CloseIcon } from "../../../assets/icons/close.svg";
import Icon, { IconVariant } from "../icon/Icon";
import { LoggingHandler } from "../../../contexts/logging/LoggingContext";

export const SIZES = ["xsmall", "small", "medium", "large"] as const;
export const VARIANT = [
    "primary", "outline-gray", "outline-blue", "ghost-blue",
    "ghost-gray", "none", "filter", "danger", "ghost-outline-blue",
    "secondary", "outline-success", "outline-danger",
] as const;

export type SizeTypes = typeof SIZES[number];
export type VariantTypes = typeof VARIANT[number];

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    size?: SizeTypes;
    variant?: VariantTypes;
    prefixIcon?: ReactNode,
    postfixIcon?: ReactNode,
    isActive?: boolean,
    isClose?: boolean
    isClickable?: boolean;
    metricPath?: string[];
}

const Button = React.forwardRef((
    args: ButtonProps,
    ref: React.ForwardedRef<HTMLButtonElement>,
): ReactElement => {
    const {
        size, variant, prefixIcon, postfixIcon, isActive, isClose, isClickable, metricPath, onClick, ...rest
    } = args;

    let icon = prefixIcon;

    if (!icon && isClose) {
        icon = <CloseIcon className="btn-icon-close" />;
    }
    const isOnlyIcon = !args.children && !postfixIcon;

    const clickHandler = metricPath ? LoggingHandler.instance.wrapMetric(metricPath, onClick) : onClick;

    return (
        <button
            type="button"
            {...rest}
            ref={ref}
            onClick={clickHandler}
            className={classNames(
                "btn",
                variant ? `btn-${variant}` : "",
                size ? `btn-${size}` : "",
                args.className,
                isOnlyIcon ? "btn-icon-only" : "",
                isActive ? "active" : "",
                isClickable === false ? "not-clickable" : "",
            )}
        >
            {icon && (
                <span className={`btn-icon ${isOnlyIcon ? "" : "btn-prefix-icon"}`}>
                    {icon}
                </span>
            )}

            {args.children}

            {postfixIcon && (
                <span className="btn-icon btn-postfix-icon">
                    {postfixIcon}
                </span>
            )}

        </button>
    );
});

const defaultProps: ButtonProps = {
    variant: "primary",
    className: "",
    prefixIcon: undefined,
    size: "large",
    isClickable: undefined,
    metricPath: undefined,
};
Button.defaultProps = defaultProps;

interface IconButtonProps extends ButtonProps {
    iconVariant?: IconVariant;
}

const Buttons = {
    Edit: (props: Omit<IconButtonProps, "prefixIcon">) => (
        <Button
            variant="none"
            size="small"
            {...props}
            prefixIcon={<Icon.Edit variant={props.iconVariant} />}
        />
    ),
    Delete: (props: Omit<IconButtonProps, "prefixIcon">) => (
        <Button
            variant="none"
            size="small"
            {...props}
            prefixIcon={<Icon.Delete variant={props.iconVariant} />}
        />
    ),
    Add: (props: Omit<IconButtonProps, "prefixIcon">) => (
        <Button
            variant="none"
            size="small"
            {...props}
            prefixIcon={<Icon.Add variant={props.iconVariant} />}
        />
    ),
    Subtract: (props: Omit<IconButtonProps, "prefixIcon">) => (
        <Button
            variant="none"
            size="small"
            {...props}
            prefixIcon={<Icon.Subtract variant={props.iconVariant} />}
        />
    ),
    Close: (props: Omit<IconButtonProps, "prefixIcon">) => (
        <Button
            variant="none"
            size="small"
            {...props}
            prefixIcon={<Icon.Close variant={props.iconVariant} />}
        />
    ),
    Next: (props: Omit<IconButtonProps, "prefixIcon">) => (
        <Button
            variant="none"
            size="small"
            {...props}
            prefixIcon={<Icon.Next variant={props.iconVariant} />}
        />
    ),
    Back: (props: Omit<IconButtonProps, "prefixIcon">) => (
        <Button
            variant="none"
            size="small"
            {...props}
            prefixIcon={<Icon.Back variant={props.iconVariant} />}
        />
    ),
    Automation: (props: Omit<IconButtonProps, "prefixIcon">) => (
        <Button
            variant="none"
            size="small"
            {...props}
            prefixIcon={<Icon.Automation variant={props.iconVariant} />}
        />
    ),
    ArtificialIntelligence: (props: Omit<IconButtonProps, "prefixIcon">) => (
        <Button
            variant="none"
            size="small"
            {...props}
            prefixIcon={<Icon.ArtificialIntelligence variant={props.iconVariant} />}
        />
    ),
};

const defaultIcon = Button as unknown as Record<string, unknown>;
Object.entries(Buttons).forEach(([key, value]) => {
    defaultIcon[key] = value;
    // (defaultIcon[key] as {defaultProps: typeof Button.defaultProps}).defaultProps = defaultProps;
});

export default Button as typeof Button & typeof Buttons;
